import { Extension } from '@tiptap/core';
import Suggestion from '@tiptap/suggestion';
import { Plugin, PluginKey } from 'prosemirror-state';

import type { VariableProps } from '../types/rich-text-editor';
import VariableNode from './variable-node';
import getVariableSuggestions from './variable-suggestions';

export interface VariableExtensionOptions {
	variables?: Array<{
		id: string;
		token: string;
		label: string;
		prefix?: string;
	}>;
	triggerChar?: string;
	pluginKeyName?: string;
	includeVariableNode?: boolean;
	variableProps?: VariableProps;
}

export function createVariableExtension(name = 'variableExtension') {
	return Extension.create<VariableExtensionOptions>({
		name,

		addOptions() {
			return {
				variables: [],
				triggerChar: '{',
				pluginKeyName: name === 'variableExtension' ? 'variable-suggestion' : `${name}-suggestion`,
				includeVariableNode: true,
			};
		},

		addExtensions() {
			return this.options.includeVariableNode === false ? [] : [VariableNode];
		},

		addProseMirrorPlugins() {
			const variables = this.options.variables || [];
			const triggerChar = this.options.triggerChar || '{';
			const pluginKeyName = this.options.pluginKeyName || 'variable-suggestion';
			const closingCharMap: Record<string, string> = {
				'{': '}',
				'[': ']',
				'(': ')',
				'<': '>',
			};
			const closingChar = closingCharMap[triggerChar];

			const suggestionPlugin = Suggestion({
				editor: this.editor,
				pluginKey: new PluginKey(pluginKeyName),
				char: triggerChar,
				startOfLine: false,
				allowSpaces: false,
				allow: ({ state, range }) => {
					const text = state.doc.textBetween(range.from, range.to);
					return text.startsWith(triggerChar);
				},
				...getVariableSuggestions(this.options.variableProps),
			});

			if (!closingChar) return [suggestionPlugin];

			return [
				suggestionPlugin,
				new Plugin({
					key: new PluginKey(`${pluginKeyName}-closing-char`),
					props: {
						handleKeyDown: (view, event) => {
							if (event.key === closingChar) {
								const { state } = view;
								const { selection } = state;
								const { from } = selection;

								let start = from - 1;
								let foundTrigger = false;

								while (start >= 0) {
									const char = state.doc.textBetween(start, start + 1);
									if (char === triggerChar) {
										foundTrigger = true;
										break;
									}
									if (char === ' ' || char === '\n') break;
									start--;
								}

								if (foundTrigger) {
									const textBetween = state.doc.textBetween(start + 1, from);
									const matchedVariable = variables.find(
										(v) =>
											v.token.toLowerCase() === textBetween.toLowerCase() ||
											v.label.toLowerCase() === textBetween.toLowerCase() ||
											v.id.toLowerCase() === textBetween.toLowerCase()
									);

									if (matchedVariable) {
										const tr = state.tr;
										tr.delete(start, from);
										tr.insert(
											start,
											state.schema.nodes.variable.create({
								id: matchedVariable.id,
								token: matchedVariable.token,
								label: matchedVariable.label,
								prefix: matchedVariable.prefix ?? this.options.variableProps?.mentionPrefix,
											})
										);
										view.dispatch(tr);
										event.preventDefault();
										return true;
									}
								}
							}
							return false;
						},
					},
				}),
			];
		},
	});
}

const VariableExtension = createVariableExtension();

export default VariableExtension;
